import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useFirebase } from '../contexts/FirebaseContext'
import { 
  BookOpen, 
  Save, 
  ArrowLeft, 
  Eye,
  Edit,
  Trash2,
  Plus
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

const NursingBulletAdd = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditMode = !!id
  
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(isEditMode)
  const [formData, setFormData] = useState({
    title: '',
    bulletCount: 0,
    createdDate: new Date().toISOString().split('T')[0]
  })
  const [bulletPoints, setBulletPoints] = useState([])
  
  const { addNursingBullet, getNursingBullets, updateNursingBullet } = useFirebase()

  useEffect(() => {
    if (isEditMode) {
      loadBulletForEdit()
    }
  }, [id])

  const loadBulletForEdit = async () => {
    try {
      setInitialLoading(true)
      const bullets = await getNursingBullets()
      const bulletToEdit = bullets.find(bullet => bullet.id === id)
      
      if (bulletToEdit) {
        setFormData({
          title: bulletToEdit.title || '',
          bulletCount: bulletToEdit.bulletCount || 0,
          createdDate: bulletToEdit.createdDate || new Date().toISOString().split('T')[0]
        })
        setBulletPoints(bulletToEdit.bulletPoints || [])
      } else {
        toast.error('Nursing bullet not found')
        navigate('/nursing-bullets')
      }
    } catch (error) {
      console.error('Error loading bullet for edit:', error)
      toast.error('Failed to load nursing bullet')
      navigate('/nursing-bullets')
    } finally {
      setInitialLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) || 0 : value
    }))
  }

  const addBulletPoint = () => {
    setBulletPoints([...bulletPoints, ''])
  }

  const removeBulletPoint = (index) => {
    setBulletPoints(bulletPoints.filter((_, i) => i !== index))
  }

  const updateBulletPoint = (index, value) => {
    const updatedPoints = [...bulletPoints]
    updatedPoints[index] = value
    setBulletPoints(updatedPoints)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.title.trim()) {
      toast.error('Please enter a title')
      return
    }

    try {
      setLoading(true)
      
      const validBulletPoints = bulletPoints.filter(point => point.trim() !== '')
      
      const bulletData = {
        title: formData.title.trim(),
        bulletCount: validBulletPoints.length,
        bulletPoints: validBulletPoints,
        createdDate: formData.createdDate,
        updatedAt: Date.now()
      }

      if (isEditMode) {
        await updateNursingBullet(id, bulletData)
        toast.success('Nursing bullet updated successfully!')
      } else {
        bulletData.createdAt = Date.now()
        bulletData.createdBy = 'admin@nursingexam.com'
        await addNursingBullet(bulletData)
        toast.success('Nursing bullet created successfully!')
      }
      
      navigate('/nursing-bullets')
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} bullet:`, error)
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} nursing bullet`)
    } finally {
      setLoading(false)
    }
  }


  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Simple Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/nursing-bullets')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-6 w-6 text-gray-600" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Edit' : 'Create'} Nursing Bullet
          </h1>
        </div>
      </div>

      {/* Form */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Bullet Details</h3>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="input w-full"
                placeholder="Enter bullet title..."
                required
              />
            </div>

            {/* Bullet Count */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bullet Count
              </label>
              <input
                type="number"
                name="bulletCount"
                value={formData.bulletCount}
                onChange={handleInputChange}
                className="input w-full"
                placeholder="Enter number of bullets (optional)..."
                min="0"
              />
            </div>

            {/* Created Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Created Date
              </label>
              <input
                type="date"
                name="createdDate"
                value={formData.createdDate}
                onChange={handleInputChange}
                className="input w-full"
              />
            </div>

            {/* Bullet Points */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  Bullet Points
                </label>
                <button
                  type="button"
                  onClick={addBulletPoint}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Point</span>
                </button>
              </div>
              
              {bulletPoints.length > 0 && (
                <div className="space-y-4">
                  {bulletPoints.map((point, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mt-2">
                        <span className="text-sm font-medium text-blue-600">{index + 1}</span>
                      </div>
                      <div className="flex-1">
                        <textarea
                          value={point}
                          onChange={(e) => updateBulletPoint(index, e.target.value)}
                          placeholder={`Enter bullet point ${index + 1}...`}
                          className="input w-full"
                          rows={3}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeBulletPoint(index)}
                        className="flex-shrink-0 p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors mt-1"
                        title="Remove bullet point"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {bulletPoints.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p>No bullet points added yet. Click "Add Point" to start.</p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate('/nursing-bullets')}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex items-center space-x-2"
              >
                {loading ? (
                  <LoadingSpinner size="small" />
                ) : (
                  <>
                    <Save className="h-5 w-5" />
                    <span>Save Bullet</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default NursingBulletAdd
